/**
 * The inspection day: the conduct dialog (Mahmoud's eighth addendum, 2026-10-11).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * His words: "conducting is a dialog with drafts: an itemized checklist the inspector
 * fills, able to SAVE UNCOMPLETED WORK FOR LATER, submitted when done." The checklist is
 * category-born on the server (the program the inspection lands in, crossed with the
 * discipline); this screen only answers it.
 *
 * PHONE FIRST. Inspectors conduct on phones, on site, in gloves: the dialog fills the
 * screen below 720px, each answer is a 48px target, the actions stay pinned under the
 * thumb, and nothing depends on hover.
 *
 * NOTHING IS LOST. The draft lives on the server (it survives a sign-out and another
 * device). Between saves, every keystroke is also kept on this device, so a closed tab
 * or a dropped signal gives the answers back on the next open, with a notice; a save
 * that would overwrite a colleague's later save is refused by the server and said here.
 *
 * NOTHING IS OFFERED THAT THE SERVER WOULD REFUSE. The sheet carries `acts`, answered by
 * each verb's predicate: Begin, Save and Submit appear only when the caller may take them.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router";

import { ApiError } from "../api/client";
import { attachToTarget, listTargetAttachments } from "../api/attachments";
import {
  getConductSheet,
  saveConductDraft,
  startConduct,
  submitConduct,
  type AnswerPayload,
  type ApprovalRoute,
  type ConductLine,
  type ConductMark,
  type ConductSheet,
  type ObservationKind,
} from "../api/conduct";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import {
  displayStatus,
  listCredentials,
  recordMeasurement,
  type Credential,
  type Measurement,
} from "../api/credentials";
import type { Me } from "../api/me";
import { ServerErrorScope, ServerField } from "../lib/serverErrors";
import { ActionDialog } from "../nav/ActionDialog";
import { RefusalBox } from "./parts";
import { AbsencePanel, LoadingLine, StateChip, day, stamp } from "./parts";
import { useQuery } from "./useQuery";
import { DownloadLink } from "./Inspections";

const copy = strings.conduct;

/** Role words joined the way a sentence reads them ("QC lead", "QC lead or Power user"). */
function roleWords(labels: readonly string[]): string {
  return labels.join(" or ");
}

/**
 * The approval chain's route in plain sentences (Mahmoud's ruling 2026-10-11): where the
 * results go, which links were skipped because nobody there may approve, or that nobody
 * above may. Empty when the server sent no route.
 */
export function approvalRouteSentences(route: ApprovalRoute | null): string[] {
  if (route === null) {
    return [];
  }
  const lines: string[] = [];
  if (route.roleLabels.length === 0) {
    lines.push(copy.routeNobody);
    return lines;
  }
  const to = roleWords(route.roleLabels);
  lines.push(route.waiting ? copy.approvalWaitsFor(to) : copy.routeBefore(to));
  if (route.skippedRoleLabels.length > 0) {
    lines.push(copy.routeSkipped(roleWords(route.skippedRoleLabels)));
  }
  return lines;
}
const MARKS: readonly ConductMark[] = ["PASS", "FAIL", "NOT_APPLICABLE"];

interface ObservationDraft {
  readonly kind: ObservationKind;
  readonly text: string;
  readonly requiresAction: boolean;
}

interface Answer {
  readonly mark: ConductMark | null;
  readonly remarks: string;
  readonly observations: readonly ObservationDraft[];
}

type Answers = Readonly<Record<string, Answer>>;

/** What this device keeps between saves: the answers, and the save they were made on. */
interface LocalCopy {
  readonly base: string | null;
  readonly answers: Answers;
}

function storageKey(verificationId: string): string {
  return `qualify.conduct.${verificationId}`;
}

function readLocal(verificationId: string): LocalCopy | null {
  try {
    const raw = localStorage.getItem(storageKey(verificationId));
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null || !("answers" in parsed)) {
      return null;
    }
    return parsed as LocalCopy;
  } catch {
    return null;
  }
}

function writeLocal(verificationId: string, copyOf: LocalCopy): void {
  try {
    localStorage.setItem(storageKey(verificationId), JSON.stringify(copyOf));
  } catch {
    // Storage full or off: the server draft still holds every saved answer.
  }
}

function clearLocal(verificationId: string): void {
  try {
    localStorage.removeItem(storageKey(verificationId));
  } catch {
    // Nothing kept, nothing to clear.
  }
}

/** The answers as the server last saved them. */
export function answersOf(sheet: ConductSheet): Answers {
  const answers: Record<string, Answer> = {};
  for (const line of sheet.lines) {
    answers[line.id] = {
      mark: line.mark,
      remarks: line.remarks ?? "",
      observations: line.observations.map((one) => ({
        kind: one.kind,
        text: one.text,
        requiresAction: one.requiresAction,
      })),
    };
  }
  return answers;
}

function sameAnswers(left: Answers, right: Answers): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** An item that failed and does not say what failed (the server refuses to submit it). */
export function unexplained(answer: Answer | undefined): boolean {
  return (
    answer?.mark === "FAIL" &&
    answer.remarks.trim() === "" &&
    !answer.observations.some((one) => one.kind === "NEGATIVE")
  );
}

function payload(sheet: ConductSheet, answers: Answers): AnswerPayload[] {
  return sheet.lines
    .filter((line) => !line.locked)
    .map((line) => {
      const answer = answers[line.id];
      return {
        line_id: line.id,
        mark: answer?.mark ?? null,
        remarks: (answer?.remarks ?? "").trim() === "" ? null : (answer?.remarks ?? "").trim(),
        observations: (answer?.observations ?? []).map((one) => ({
          kind: one.kind,
          text: one.text,
          requires_action: one.kind === "NEGATIVE" && one.requiresAction,
        })),
      };
    });
}

export function ConductDialog({
  verificationId,
  onClose,
  onChanged,
  canDelegate,
  onDelegate,
}: {
  readonly verificationId: string;
  readonly onClose: () => void;
  /** After a submission (or a begin), so the page behind re-reads the record. */
  readonly onChanged: (message: string) => void;
  readonly canDelegate?: boolean;
  readonly onDelegate?: () => void;
}): React.JSX.Element {
  const token = useToken();
  const [reload, setReload] = useState(0);
  const [load, setLoad] = useState<"loading" | "ready" | "absence" | "failed">("loading");
  const [sheet, setSheet] = useState<ConductSheet | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [restoredNotice, setRestoredNotice] = useState<"restored" | "stale" | null>(null);
  const [busy, setBusy] = useState<"begin" | "save" | "submit" | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // A fresh read (or a save's answer) becomes the form, with this device's unsaved
  // answers laid back over it when they were made on the same save.
  const adopt = useCallback(
    (next: ConductSheet, fromServerOnly: boolean): void => {
      setSheet(next);
      const saved = answersOf(next);
      const local = fromServerOnly ? null : readLocal(verificationId);
      if (local !== null && !sameAnswers(local.answers, saved)) {
        if (local.base === next.savedAt) {
          setAnswers({ ...saved, ...local.answers });
          setRestoredNotice("restored");
          return;
        }
        setRestoredNotice("stale");
      } else {
        setRestoredNotice(null);
      }
      setAnswers(saved);
    },
    [verificationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    getConductSheet(token, verificationId, controller.signal)
      .then((next) => {
        adopt(next, false);
        setLoad("ready");
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setLoad(caught instanceof ApiError && caught.kind === "absence" ? "absence" : "failed");
      });
    return () => {
      controller.abort();
    };
  }, [token, verificationId, reload, adopt]);

  const update = (lineId: string, change: Partial<Answer>): void => {
    setSavedNotice(false);
    setAnswers((current) => {
      const before = current[lineId] ?? { mark: null, remarks: "", observations: [] };
      const next = { ...current, [lineId]: { ...before, ...change } };
      if (sheet !== null) {
        writeLocal(verificationId, { base: sheet.savedAt, answers: next });
      }
      return next;
    });
  };

  const begin = async (): Promise<void> => {
    setBusy("begin");
    setError(null);
    try {
      adopt(await startConduct(token, verificationId), false);
      onChanged("");
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(null);
    }
  };

  const save = async (): Promise<void> => {
    if (sheet === null) {
      return;
    }
    setBusy("save");
    setError(null);
    try {
      const next = await saveConductDraft(token, verificationId, {
        saved_at: sheet.savedAt,
        answers: payload(sheet, answers),
      });
      clearLocal(verificationId);
      adopt(next, true);
      setSavedNotice(true);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(null);
    }
  };

  const submit = async (): Promise<void> => {
    if (sheet === null) {
      return;
    }
    setBusy("submit");
    setError(null);
    try {
      await submitConduct(token, verificationId, {
        saved_at: sheet.savedAt,
        answers: payload(sheet, answers),
      });
      clearLocal(verificationId);
      onChanged(copy.submitted);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
        setConfirming(false);
      }
    } finally {
      setBusy(null);
    }
  };

  const discardLocal = (): void => {
    clearLocal(verificationId);
    if (sheet !== null) {
      setAnswers(answersOf(sheet));
    }
    setRestoredNotice(null);
  };

  const restoreAnyway = (): void => {
    const local = readLocal(verificationId);
    if (local !== null && sheet !== null) {
      setAnswers({ ...answersOf(sheet), ...local.answers });
      writeLocal(verificationId, { base: sheet.savedAt, answers: local.answers });
    }
    setRestoredNotice("restored");
  };

  const title = copy.title(sheet?.humanId ?? "");
  const stale = error?.problem?.rule === "conduct.draft_changed";

  return (
    <ActionDialog
      open
      fullSheet
      dismissOnOverlay={false}
      title={title}
      onClose={onClose}
      error={error ?? undefined}
    >
      {load === "loading" && sheet === null && <LoadingLine label={strings.common.loading} />}
      {load === "absence" && <AbsencePanel />}
      {load === "failed" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {sheet !== null && (
        <ConductBody
          sheet={sheet}
          answers={answers}
          busy={busy}
          confirming={confirming}
          savedNotice={savedNotice}
          restoredNotice={restoredNotice}
          stale={stale}
          onBegin={() => {
            void begin();
          }}
          onUpdate={update}
          onSave={() => {
            void save();
          }}
          onAskSubmit={() => {
            setError(null);
            setConfirming(true);
          }}
          onCancelSubmit={() => {
            setConfirming(false);
          }}
          onSubmit={() => {
            void submit();
          }}
          onDiscardLocal={discardLocal}
          onRestoreAnyway={restoreAnyway}
          onReload={() => {
            setError(null);
            setReload((n) => n + 1);
          }}
          onClose={onClose}
          canDelegate={canDelegate}
          onDelegate={onDelegate}
        />
      )}
    </ActionDialog>
  );
}

function ConductBody({
  sheet,
  answers,
  busy,
  confirming,
  savedNotice,
  restoredNotice,
  stale,
  canDelegate,
  onDelegate,
  onBegin,
  onUpdate,
  onSave,
  onAskSubmit,
  onCancelSubmit,
  onSubmit,
  onDiscardLocal,
  onRestoreAnyway,
  onReload,
  onClose,
}: {
  readonly sheet: ConductSheet;
  readonly answers: Answers;
  readonly busy: "begin" | "save" | "submit" | null;
  readonly confirming: boolean;
  readonly savedNotice: boolean;
  readonly restoredNotice: "restored" | "stale" | null;
  readonly stale: boolean;
  readonly canDelegate?: boolean | undefined;
  readonly onDelegate?: (() => void) | undefined;
  readonly onBegin: () => void;
  readonly onUpdate: (lineId: string, change: Partial<Answer>) => void;
  readonly onSave: () => void;
  readonly onAskSubmit: () => void;
  readonly onCancelSubmit: () => void;
  readonly onSubmit: () => void;
  readonly onDiscardLocal: () => void;
  readonly onRestoreAnyway: () => void;
  readonly onReload: () => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const facts = (
    <dl className="q-conduct__facts" data-testid="conduct-facts">
      <div>
        <dt>{copy.program}</dt>
        <dd>{sheet.programLabel}</dd>
      </div>
      {sheet.disciplineLabel !== null && (
        <div>
          <dt>{copy.discipline}</dt>
          <dd>{sheet.disciplineLabel}</dd>
        </div>
      )}
      <div>
        <dt>{copy.contract}</dt>
        <dd>{sheet.contractHumanId}</dd>
      </div>
      <div>
        <dt>{copy.contractor}</dt>
        <dd>{sheet.contractorPartyName}</dd>
      </div>
      <div>
        <dt>{copy.date}</dt>
        <dd>{day(sheet.plannedDate)}</dd>
      </div>
      {sheet.location !== null && (
        <div>
          <dt>{copy.location}</dt>
          <dd>{sheet.location}</dd>
        </div>
      )}
      {sheet.activityHumanId !== null && (
        <div>
          <dt>{copy.activity}</dt>
          <dd>{sheet.activityHumanId}</dd>
        </div>
      )}
      <div>
        <dt>{copy.checklist}</dt>
        <dd data-testid="conduct-checklist">
          {sheet.checklistCode === null
            ? copy.noChecklistName
            : `${sheet.checklistCode} ${sheet.checklistTitle ?? ""}`}
        </dd>
      </div>
    </dl>
  );

  if (sheet.acts.includes("START")) {
    return (
      <div className="q-conduct" data-testid="conduct-intro">
        <p className="q-shell__lede">
          {sheet.title} <StateChip state={sheet.workflowState} />
        </p>
        {facts}
        <h4 className="q-conduct__section">{copy.introHeading}</h4>
        <p>{copy.introBody}</p>
        <div className="q-conduct__footer">
          <div className="q-conduct__footer-actions">
            <button type="button" className="q-button q-button--quiet" onClick={onClose}>
              {copy.close}
            </button>
            {canDelegate && onDelegate && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="conduct-delegate"
                onClick={onDelegate}
              >
                {strings.rfis.delegate}
              </button>
            )}
            <button
              type="button"
              className="q-button q-mini--primary"
              data-testid="conduct-begin"
              disabled={busy !== null}
              aria-busy={busy === "begin"}
              onClick={onBegin}
            >
              {busy === "begin" ? copy.beginBusy : copy.beginNow}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!sheet.acts.includes("SAVE")) {
    return (
      <div className="q-conduct" data-testid="conduct-closed">
        <p className="q-shell__lede">
          {sheet.title} <StateChip state={sheet.workflowState} />
        </p>
        {facts}
        <p className="q-note">{copy.notOpen}</p>
      </div>
    );
  }

  return (
    <ConductForm
      sheet={sheet}
      facts={facts}
      answers={answers}
      busy={busy}
      confirming={confirming}
      savedNotice={savedNotice}
      restoredNotice={restoredNotice}
      stale={stale}
      canDelegate={canDelegate}
      onDelegate={onDelegate}
      onUpdate={onUpdate}
      onSave={onSave}
      onAskSubmit={onAskSubmit}
      onCancelSubmit={onCancelSubmit}
      onSubmit={onSubmit}
      onDiscardLocal={onDiscardLocal}
      onRestoreAnyway={onRestoreAnyway}
      onReload={onReload}
    />
  );
}

/** The checklist, grouped by the bank's numbered sections, in the bank's order. */
function sectionsOf(
  lines: readonly ConductLine[],
): { key: string; heading: string; lines: ConductLine[] }[] {
  const groups: { key: string; heading: string; lines: ConductLine[] }[] = [];
  for (const line of lines) {
    const key = line.fromChecklist ? `section-${line.sectionNumber ?? ""}` : "requested";
    const heading = line.fromChecklist
      ? copy.sectionHeading(line.sectionNumber, line.sectionTitle)
      : copy.requestedPoint;
    const last = groups[groups.length - 1];
    if (last?.key === key) {
      last.lines.push(line);
    } else {
      groups.push({ key, heading, lines: [line] });
    }
  }
  return groups;
}

function ConductForm({
  sheet,
  facts,
  answers,
  busy,
  confirming,
  savedNotice,
  restoredNotice,
  stale,
  canDelegate,
  onDelegate,
  onUpdate,
  onSave,
  onAskSubmit,
  onCancelSubmit,
  onSubmit,
  onDiscardLocal,
  onRestoreAnyway,
  onReload,
}: {
  readonly sheet: ConductSheet;
  readonly facts: React.ReactNode;
  readonly answers: Answers;
  readonly busy: "begin" | "save" | "submit" | null;
  readonly confirming: boolean;
  readonly savedNotice: boolean;
  readonly restoredNotice: "restored" | "stale" | null;
  readonly stale: boolean;
  readonly canDelegate?: boolean | undefined;
  readonly onDelegate?: (() => void) | undefined;
  readonly onUpdate: (lineId: string, change: Partial<Answer>) => void;
  readonly onSave: () => void;
  readonly onAskSubmit: () => void;
  readonly onCancelSubmit: () => void;
  readonly onSubmit: () => void;
  readonly onDiscardLocal: () => void;
  readonly onRestoreAnyway: () => void;
  readonly onReload: () => void;
}): React.JSX.Element {
  const groups = useMemo(() => sectionsOf(sheet.lines), [sheet.lines]);
  // Wave CRED ruling 17: the measured values are the operator's (the server asks the
  // conduct's SAVE act of an internal caller); the instruments offered are the VALID
  // calibrations of the register. A counterparty's sheet carries no measurement form.
  const token = useToken();
  const outlet = useOutletContext<{ me?: Me | null } | undefined>();
  const workflowClass = outlet?.me?.workflowClass ?? null;
  const measures = workflowClass !== "contractor" && workflowClass !== "tpi";
  const instruments = useQuery(
    (signal) =>
      measures ? listCredentials(token, { holderKind: "INSTRUMENT", signal }) : Promise.resolve([]),
    [token, measures],
  );
  const validInstruments =
    instruments.kind === "ready"
      ? instruments.data.filter((one) => displayStatus(one) === "VALID")
      : [];
  const [measured, setMeasured] = useState<Readonly<Record<string, Measurement>>>({});
  const total = sheet.lines.length;
  const answered = sheet.lines.filter((line) => {
    const one = answers[line.id]?.mark;
    return one !== null && one !== undefined;
  }).length;
  const failed = sheet.lines.filter((line) => answers[line.id]?.mark === "FAIL").length;
  const missingReason = sheet.lines.filter((line) => unexplained(answers[line.id])).length;
  const observations = sheet.lines.reduce(
    (sum, line) => sum + (answers[line.id]?.observations.length ?? 0),
    0,
  );
  const maySubmit = sheet.acts.includes("SUBMIT");
  const submitBlocked = !maySubmit || answered < total || missingReason > 0;

  return (
    <div className="q-conduct" data-testid="conduct-form">
      {facts}
      {sheet.returnedReason !== null && (
        <div className="q-banner" role="status" data-testid="conduct-returned">
          <p className="q-banner__heading">{copy.returnedHeading}</p>
          <p className="q-thread__body">{sheet.returnedReason}</p>
        </div>
      )}
      {restoredNotice === "restored" && (
        <div className="q-banner" role="status" data-testid="conduct-restored">
          <p className="q-thread__body">{copy.restored}</p>
          <button type="button" className="q-button q-button--quiet" onClick={onDiscardLocal}>
            {copy.discardLocal}
          </button>
        </div>
      )}
      {restoredNotice === "stale" && (
        <div className="q-banner" role="status" data-testid="conduct-restored-stale">
          <p className="q-thread__body">{copy.restoredStale}</p>
          <div className="q-actions-row">
            <button type="button" className="q-button q-button--quiet" onClick={onRestoreAnyway}>
              {copy.restoreAnyway}
            </button>
            <button type="button" className="q-button q-button--quiet" onClick={onDiscardLocal}>
              {copy.discardLocal}
            </button>
          </div>
        </div>
      )}
      <p className="q-note" data-testid="conduct-saved-line">
        {sheet.savedAt === null
          ? copy.notSavedYet
          : copy.savedBy(sheet.savedByName ?? "", stamp(sheet.savedAt))}
      </p>
      <p className="q-conduct__progress" data-testid="conduct-progress" aria-live="polite">
        <span>{copy.progress(answered, total)}</span>
        {failed > 0 && <span className="q-chip q-chip--danger">{copy.failedCount(failed)}</span>}
      </p>

      {groups.map((group) => (
        <section key={group.key} data-testid="conduct-section">
          <h4 className="q-conduct__section">{group.heading}</h4>
          <div className="q-conduct__list">
            {group.lines.map((line) => (
              <ConductItem
                key={line.id}
                verificationId={sheet.id}
                line={line}
                answer={answers[line.id]}
                measurement={measured[line.id] ?? line.measurement}
                measures={measures}
                instruments={validInstruments}
                onMeasured={(next) => {
                  setMeasured((current) => ({ ...current, [line.id]: next }));
                }}
                onUpdate={(change) => {
                  onUpdate(line.id, change);
                }}
              />
            ))}
          </div>
        </section>
      ))}

      <div className="q-conduct__footer" data-testid="conduct-footer">
        {stale && (
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="conduct-reload"
            onClick={onReload}
          >
            {copy.reload}
          </button>
        )}
        {savedNotice && (
          <p className="q-note" role="status" data-testid="conduct-saved">
            {copy.saved}
          </p>
        )}
        {confirming ? (
          <div className="q-conduct__list" data-testid="conduct-submit-confirm">
            <p className="q-conduct__strong">{copy.submitHeading}</p>
            <p>{copy.submitLede}</p>
            <p data-testid="conduct-submit-findings">{copy.submitFindings(failed)}</p>
            <p>{copy.submitObservations(observations)}</p>
            {approvalRouteSentences(sheet.approvalRoute).map((sentence) => (
              <p key={sentence} data-testid="conduct-submit-route">
                {sentence}
              </p>
            ))}
            <div className="q-conduct__footer-actions">
              <button
                type="button"
                className="q-button q-button--quiet"
                disabled={busy !== null}
                onClick={onCancelSubmit}
              >
                {strings.common.cancel}
              </button>
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="conduct-submit-send"
                disabled={busy !== null}
                aria-busy={busy === "submit"}
                onClick={onSubmit}
              >
                {busy === "submit" ? copy.submitBusy : copy.submitConfirm}
              </button>
            </div>
          </div>
        ) : (
          <>
            {submitBlocked && (
              <p className="q-note" data-testid="conduct-submit-why">
                {!maySubmit
                  ? copy.submitBlockedNoRight
                  : [
                      answered < total ? copy.submitBlockedUnanswered(total - answered) : null,
                      missingReason > 0 ? copy.submitBlockedUnexplained(missingReason) : null,
                    ]
                      .filter((one) => one !== null)
                      .join(" ")}
              </p>
            )}
            <div className="q-conduct__footer-actions">
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="conduct-save"
                disabled={busy !== null}
                aria-busy={busy === "save"}
                onClick={onSave}
              >
                {busy === "save" ? copy.saving : copy.save}
              </button>
              {canDelegate && onDelegate && (
                <button
                  type="button"
                  className="q-button q-button--quiet"
                  data-testid="conduct-delegate"
                  onClick={onDelegate}
                >
                  {strings.rfis.delegate}
                </button>
              )}
              {maySubmit && (
                <button
                  type="button"
                  className="q-button q-mini--primary"
                  data-testid="conduct-submit"
                  disabled={busy !== null || submitBlocked}
                  aria-disabled={busy !== null || submitBlocked}
                  onClick={onAskSubmit}
                >
                  {copy.submit}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ConductItem({
  verificationId,
  line,
  answer,
  measurement,
  measures,
  instruments,
  onMeasured,
  onUpdate,
}: {
  readonly verificationId: string;
  readonly line: ConductLine;
  readonly answer: Answer | undefined;
  readonly measurement: Measurement | null;
  readonly measures: boolean;
  readonly instruments: readonly Credential[];
  readonly onMeasured: (next: Measurement) => void;
  readonly onUpdate: (change: Partial<Answer>) => void;
}): React.JSX.Element {
  const mark = answer?.mark ?? null;
  const remarks = answer?.remarks ?? "";
  const observations = answer?.observations ?? [];
  const [adding, setAdding] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const needsReason = unexplained(answer);
  const tone =
    mark === "FAIL" ? " q-conduct__item--fail" : mark === "PASS" ? " q-conduct__item--pass" : "";
  const remarksId = `conduct-remarks-${line.id}`;

  return (
    <article className={`q-conduct__item${tone}`} data-testid="conduct-item">
      <div className="q-conduct__item-head">
        <span className="q-conduct__number">{copy.itemLabel(line.number)}</span>
        <span>{line.description}</span>
      </div>
      <p className="q-conduct__criteria">
        {copy.acceptance}: {line.acceptanceCriteria}
      </p>
      {(measures || measurement !== null) && (
        <MeasurementBlock
          verificationId={verificationId}
          line={line}
          measurement={measurement}
          editable={measures && !line.locked}
          instruments={instruments}
          onMeasured={onMeasured}
        />
      )}
      {measurement !== null && !measurement.withinTolerance && mark === "PASS" && (
        <p className="q-conduct__hint" data-testid="measurement-pass-hint">
          {strings.credentials.outsideHint}
        </p>
      )}
      <fieldset className="q-conduct__marks" disabled={line.locked}>
        <legend className="q-visually-hidden">{copy.markGroup(line.number)}</legend>
        {MARKS.map((one) => (
          <label key={one} className={`q-conduct__mark q-conduct__mark--${one}`}>
            <input
              type="radio"
              name={`mark-${line.id}`}
              value={one}
              checked={mark === one}
              data-testid={`conduct-mark-${one}`}
              onChange={() => {
                onUpdate({ mark: one });
              }}
            />
            {copy.marks[one]}
          </label>
        ))}
      </fieldset>
      <div className="q-field">
        <label className="q-field__label" htmlFor={remarksId}>
          {mark === "FAIL" ? copy.remarksFailLabel : copy.remarksLabel}
        </label>
        <ServerField field="remarks">
          <textarea
            id={remarksId}
            className="q-textarea"
            data-testid="conduct-remarks"
            rows={2}
            value={remarks}
            disabled={line.locked}
            aria-invalid={needsReason || undefined}
            onChange={(event) => {
              onUpdate({ remarks: event.target.value });
            }}
          />
        </ServerField>
        {needsReason && (
          <p className="q-conduct__hint" data-testid="conduct-needs-reason">
            {copy.failNeedsReason}
          </p>
        )}
      </div>

      {observations.length > 0 && (
        <div>
          <p className="q-field__label">{copy.observationsHeading}</p>
          <ul className="q-conduct__observations" data-testid="conduct-observations">
            {observations.map((one, index) => (
              <li
                key={`${one.kind}-${String(index)}`}
                className={`q-conduct__observation q-conduct__observation--${one.kind}`}
              >
                <span>
                  <strong>{copy.observationKinds[one.kind]}</strong>{" "}
                  {one.kind === "NEGATIVE" &&
                    `(${one.requiresAction ? copy.observationForAction : copy.observationForInformation}) `}
                  {one.text}
                </span>
                <button
                  type="button"
                  className="q-button q-button--quiet"
                  aria-label={copy.observationRemove(one.text)}
                  onClick={() => {
                    onUpdate({ observations: observations.filter((_, at) => at !== index) });
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {adding ? (
        <ObservationForm
          lineId={line.id}
          onAdd={(one) => {
            onUpdate({ observations: [...observations, one] });
            setAdding(false);
          }}
          onCancel={() => {
            setAdding(false);
          }}
        />
      ) : (
        <div className="q-actions-row">
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="conduct-observation-add"
            disabled={line.locked}
            onClick={() => {
              setAdding(true);
            }}
          >
            {copy.observationAdd}
          </button>
          <button
            type="button"
            className="q-button q-button--quiet"
            aria-expanded={evidenceOpen}
            data-testid="conduct-evidence-toggle"
            onClick={() => {
              setEvidenceOpen((value) => !value);
            }}
          >
            {copy.evidenceToggle}
          </button>
        </div>
      )}
      {evidenceOpen && <ItemEvidence verificationId={verificationId} lineId={line.id} />}
    </article>
  );
}

function ObservationForm({
  lineId,
  onAdd,
  onCancel,
}: {
  readonly lineId: string;
  readonly onAdd: (one: ObservationDraft) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [kind, setKind] = useState<ObservationKind>("NEGATIVE");
  const [text, setText] = useState("");
  const [requiresAction, setRequiresAction] = useState(false);
  const textId = `conduct-observation-${lineId}`;
  return (
    <div className="q-conduct__mini" data-testid="conduct-observation-form">
      <fieldset className="q-conduct__marks q-conduct__marks--two">
        <legend className="q-field__label">{copy.observationKindLabel}</legend>
        {(["POSITIVE", "NEGATIVE"] as const).map((one) => (
          <label
            key={one}
            className={`q-conduct__mark q-conduct__mark--${one === "POSITIVE" ? "PASS" : "FAIL"}`}
          >
            <input
              type="radio"
              name={`observation-kind-${lineId}`}
              checked={kind === one}
              data-testid={`conduct-observation-${one}`}
              onChange={() => {
                setKind(one);
              }}
            />
            {copy.observationKinds[one]}
          </label>
        ))}
      </fieldset>
      <label className="q-field__label" htmlFor={textId}>
        {copy.observationTextLabel}
      </label>
      <ServerField field="observations">
        <textarea
          id={textId}
          className="q-textarea"
          data-testid="conduct-observation-text"
          rows={2}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
        />
      </ServerField>
      {kind === "NEGATIVE" && (
        <div>
          <label className="q-check">
            <input
              type="checkbox"
              checked={requiresAction}
              data-testid="conduct-observation-action"
              onChange={(event) => {
                setRequiresAction(event.target.checked);
              }}
            />
            {copy.observationActionLabel}
          </label>
          <p className="q-conduct__criteria">{copy.observationActionHelper}</p>
        </div>
      )}
      <div className="q-actions-row">
        <button type="button" className="q-button q-button--quiet" onClick={onCancel}>
          {copy.observationCancel}
        </button>
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="conduct-observation-save"
          disabled={text.trim() === ""}
          onClick={() => {
            onAdd({
              kind,
              text: text.trim(),
              requiresAction: kind === "NEGATIVE" && requiresAction,
            });
          }}
        >
          {copy.observationSave}
        </button>
      </div>
    </div>
  );
}

/** The evidence on one item: read when opened, uploaded at once (several files a pick). */
function ItemEvidence({
  verificationId,
  lineId,
}: {
  readonly verificationId: string;
  readonly lineId: string;
}): React.JSX.Element {
  const token = useToken();
  const [stampKey, setStampKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const files = useQuery(
    (signal) =>
      listTargetAttachments(token, { entityType: "VERIFICATION_LINE", entityId: lineId }, signal),
    [token, lineId, stampKey],
  );
  const inputId = `conduct-evidence-${verificationId}-${lineId}`;
  const upload = async (picked: readonly File[]): Promise<void> => {
    if (picked.length === 0) {
      return;
    }
    setUploading(true);
    setError(null);
    try {
      for (const file of picked) {
        await attachToTarget(token, file, {
          entityType: "VERIFICATION_LINE",
          entityId: lineId,
          purpose: "EVIDENCE",
        });
      }
      setStampKey((n) => n + 1);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="q-conduct__mini" data-testid="conduct-evidence">
      {files.kind === "loading" && <LoadingLine label={strings.common.loading} />}
      {files.kind === "ready" &&
        (files.data.length === 0 ? (
          <p className="q-note">{copy.evidenceEmpty}</p>
        ) : (
          <ul className="q-conduct__observations">
            {files.data.map((one) => (
              <li key={one.linkId}>
                <DownloadLink attachmentId={one.attachmentId} filename={one.filename} />
              </li>
            ))}
          </ul>
        ))}
      {error !== null && (
        <p className="q-conduct__hint" role="alert">
          {error.problem?.detail ?? strings.inspections.attachmentFailure}
        </p>
      )}
      <label className="q-button q-button--quiet" htmlFor={inputId}>
        {uploading ? copy.evidenceUploading : copy.evidenceAttach}
      </label>
      <input
        id={inputId}
        type="file"
        multiple
        className="q-visually-hidden"
        data-testid="conduct-evidence-input"
        onChange={(event) => {
          void upload(Array.from(event.target.files ?? []).filter((one) => one.size > 0));
          event.target.value = "";
        }}
      />
    </div>
  );
}

/**
 * Wave CRED ruling 17: the measured value of one line, its unit, the tolerance it is
 * judged against and the calibrated instrument that took it, recorded on its own (PUT
 * /api/verifications/{id}/lines/{line}/measurement) and judged by the server
 * (`within_tolerance`). An out-of-tolerance line is never passed: the save and submit
 * refuse it (409 conduct.measurement_out_of_tolerance), said in the sheet in the server's
 * words; this block's own refusals (measurement.contract_state, credential.expired, the
 * unit and the tolerance order) are said here.
 */
function MeasurementBlock({
  verificationId,
  line,
  measurement,
  editable,
  instruments,
  onMeasured,
}: {
  readonly verificationId: string;
  readonly line: ConductLine;
  readonly measurement: Measurement | null;
  readonly editable: boolean;
  readonly instruments: readonly Credential[];
  readonly onMeasured: (next: Measurement) => void;
}): React.JSX.Element {
  const token = useToken();
  const words = strings.credentials;
  const [value, setValue] = useState(measurement?.value ?? "");
  const [unit, setUnit] = useState(measurement?.unit ?? "");
  const [low, setLow] = useState(measurement?.toleranceMin ?? "");
  const [high, setHigh] = useState(measurement?.toleranceMax ?? "");
  const [instrument, setInstrument] = useState(measurement?.instrumentCredentialId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const ready = value.trim() !== "" && unit.trim() !== "";
  const id = (name: string): string => `measurement-${name}-${line.id}`;

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const next = await recordMeasurement(token, verificationId, line.id, {
        value: value.trim(),
        unit: unit.trim(),
        tolerance_min: low.trim() === "" ? null : low.trim(),
        tolerance_max: high.trim() === "" ? null : high.trim(),
        instrument_credential_id: instrument === "" ? null : instrument,
      });
      onMeasured(next);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="q-subform" data-testid="measurement">
      <p className="q-field__label">
        {words.measurementHeading}{" "}
        {measurement !== null && (
          <span
            className={`q-chip ${measurement.withinTolerance ? "q-chip--success" : "q-chip--danger"}`}
            data-testid="measurement-verdict"
            data-within={measurement.withinTolerance ? "true" : "false"}
          >
            {measurement.withinTolerance ? words.within : words.outside}
          </span>
        )}
      </p>
      {measurement !== null && (
        <p className="q-note" data-testid="measurement-recorded">
          {words.measuredAs(
            measurement.value,
            measurement.unit,
            measurement.toleranceMin,
            measurement.toleranceMax,
          )}
        </p>
      )}
      {editable && (
        <ServerErrorScope error={error}>
          <div className="q-form-grid">
            <div className="q-field">
              <label className="q-field__label" htmlFor={id("value")}>
                {words.valueLabel}
              </label>
              <ServerField field="value">
                <input
                  id={id("value")}
                  className="q-input"
                  inputMode="decimal"
                  data-testid="measurement-value"
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value);
                  }}
                />
              </ServerField>
            </div>
            <div className="q-field">
              <label className="q-field__label" htmlFor={id("unit")}>
                {words.unitLabel}
              </label>
              <ServerField field="unit">
                <input
                  id={id("unit")}
                  className="q-input"
                  data-testid="measurement-unit"
                  value={unit}
                  onChange={(event) => {
                    setUnit(event.target.value);
                  }}
                />
              </ServerField>
            </div>
            <div className="q-field">
              <label className="q-field__label" htmlFor={id("min")}>
                {words.toleranceMinLabel}
              </label>
              <ServerField field="tolerance_min">
                <input
                  id={id("min")}
                  className="q-input"
                  inputMode="decimal"
                  data-testid="measurement-min"
                  value={low}
                  onChange={(event) => {
                    setLow(event.target.value);
                  }}
                />
              </ServerField>
            </div>
            <div className="q-field">
              <label className="q-field__label" htmlFor={id("max")}>
                {words.toleranceMaxLabel}
              </label>
              <ServerField field="tolerance_max">
                <input
                  id={id("max")}
                  className="q-input"
                  inputMode="decimal"
                  data-testid="measurement-max"
                  value={high}
                  onChange={(event) => {
                    setHigh(event.target.value);
                  }}
                />
              </ServerField>
            </div>
            <div className="q-field">
              <label className="q-field__label" htmlFor={id("instrument")}>
                {words.instrumentPickLabel}
              </label>
              <ServerField field="instrument_credential_id">
                <select
                  id={id("instrument")}
                  className="q-input"
                  data-testid="measurement-instrument"
                  value={instrument}
                  onChange={(event) => {
                    setInstrument(event.target.value);
                  }}
                >
                  <option value="">{words.instrumentNone}</option>
                  {instruments.map((one) => (
                    <option key={one.id} value={one.id}>
                      {words.instrumentOption(one.instrumentTag ?? one.reference, one.validUntil)}
                    </option>
                  ))}
                </select>
              </ServerField>
            </div>
          </div>
          {!ready && <p className="q-note">{words.measurementNeeded}</p>}
          {error !== null && <RefusalBox error={error} />}
          <button
            type="button"
            className="q-button q-button--quiet"
            data-testid="measurement-save"
            disabled={busy || !ready}
            onClick={() => {
              void save();
            }}
          >
            {words.measurementSave}
          </button>
        </ServerErrorScope>
      )}
    </div>
  );
}
